import { CheckCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../utils/axios";
import { Button } from "./ui/button";

const PaymentSuccess: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionType, setSubscriptionType] = useState<string>("");
  const location = useLocation();
  const navigate = useNavigate();
  const { premiumType, setPremiumType } = useAuth();

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get the session_id from URL query parameters
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");

        if (!sessionId) {
          setError("Missing session ID");
          setLoading(false);
          return;
        }

        // Try to verify the payment multiple times with a delay
        let attempts = 0;
        const maxAttempts = 5;
        const delay = 2000; // 2 seconds

        const checkPayment = async () => {
          try {
            // Verify the payment with the backend
            const response = await api.get("/stripe/subscription-status");
            const { premium_type } = response.data;

            if (premium_type !== "none") {
              // Payment was successful and processed
              setSubscriptionType(premium_type);
              localStorage.setItem("premiumType", premium_type);
              setPremiumType(premium_type); // Update the auth context
              setLoading(false);
              return true;
            }

            // If we haven't reached max attempts, try again after delay
            if (attempts < maxAttempts) {
              attempts++;
              setTimeout(checkPayment, delay);
              return false;
            }

            // If we've reached max attempts and still no success
            setError(
              "No se pudo verificar el pago. Por favor, contacta con soporte."
            );
            setLoading(false);
            return true;
          } catch (error) {
            console.error("Error verifying payment:", error);
            if (attempts < maxAttempts) {
              attempts++;
              setTimeout(checkPayment, delay);
              return false;
            }
            setError(
              "Error al verificar el pago. Por favor, contacta con soporte."
            );
            setLoading(false);
            return true;
          }
        };

        // Start the verification process
        checkPayment();
      } catch (error) {
        console.error("Error in payment verification:", error);
        setError(
          "Error al verificar el pago. Por favor, contacta con soporte."
        );
        setLoading(false);
      }
    };

    verifyPayment();
  }, [location, setPremiumType]);

  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-lg">Verificando el pago...</p>
        <p className="text-sm text-gray-500 mt-2">
          Esto puede tardar unos segundos
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold mb-4">Error en el pago</h1>
        <p className="text-lg text-center mb-6">{error}</p>
        <Button onClick={handleGoToDashboard}>Volver al Inicio</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
      <CheckCircle className="text-green-500 h-20 w-20 mb-4" />
      <h1 className="text-2xl font-bold mb-4">¡Pago Exitoso!</h1>
      <p className="text-lg text-center mb-2">
        ¡Gracias por tu suscripción! Tu cuenta ha sido actualizada a Premium{" "}
        {subscriptionType === "pro" ? "Pro" : "Basic"}.
      </p>
      <p className="text-md text-center mb-6">
        Ahora tienes acceso a todas las funcionalidades premium de BOE Fácil.
      </p>
      <Button onClick={handleGoToDashboard} className="mb-4">
        Ir al Dashboard
      </Button>
      <p className="text-sm text-gray-500">
        Si tienes alguna pregunta, por favor contacta con nuestro soporte.
      </p>
    </div>
  );
};

export default PaymentSuccess;
